import {
  RenderedClauseText,
  useStaffUpdateContractSpecialConditionDueDate,
} from '@urbanx/agx.campaigns.client'
import {
  AgxButton,
  AgxDatePicker,
  AgxDivider,
  AgxSaveChangesButton,
  AgxSpacer,
  AgxTypography,
  SAVE_BUTTON_STATES,
} from '@urbanx/agx.ui-components2'
import { useCallback, useEffect, useMemo, useState } from 'react'

interface SpecialConditionClauseProps {
  clause: RenderedClauseText
  conditionId: string
  contractId: string
  campaignId: string
  isLastClause: boolean
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDate: Date | undefined
  ) => void
}

const SpecialConditionClause = ({
  clause,
  conditionId,
  contractId,
  campaignId,
  isLastClause,
  onDueDateUpdated,
}: SpecialConditionClauseProps) => {
  const [isEditing, setIsEditing] = useState(false)

  const onEditClick = useCallback(() => setIsEditing(true), [])

  const [saveState, setSaveState] = useState<SAVE_BUTTON_STATES>(
    SAVE_BUTTON_STATES.UNSAVED
  )

  const saveButtonText = useMemo(() => {
    switch (saveState) {
      case SAVE_BUTTON_STATES.SAVED:
        return 'Changes Saved'
      case SAVE_BUTTON_STATES.UNSAVED:
        return 'Save'
      case SAVE_BUTTON_STATES.ERROR:
        return 'Error'
      case SAVE_BUTTON_STATES.SAVING:
        return 'Saving...'
      default:
        return 'Save Changes'
    }
  }, [saveState])

  useEffect(() => {
    if (!isEditing) setSaveState(SAVE_BUTTON_STATES.UNSAVED)
  }, [isEditing])

  const [localDueDate, setLocalDueDate] = useState<Date | undefined>(
    () => clause.dueDate
  )

  const { staffUpdateContractSpecialConditionDueDate } =
    useStaffUpdateContractSpecialConditionDueDate({
      onSuccess: async () => {
        setSaveState(SAVE_BUTTON_STATES.SAVED)
        setIsEditing(false)
        onDueDateUpdated(conditionId, clause.clauseId ?? '', localDueDate)
      },
      onError: () => {
        setSaveState(SAVE_BUTTON_STATES.ERROR)
        setIsEditing(false)
      },
    })

  const onSaveClick = useCallback(() => {
    if (!clause?.clauseId) return
    if (saveState !== SAVE_BUTTON_STATES.UNSAVED) return

    setSaveState(SAVE_BUTTON_STATES.SAVING)

    staffUpdateContractSpecialConditionDueDate({
      campaignId: campaignId,
      contractId,
      conditionId,
      clauseId: clause.clauseId,
      dueDate: localDueDate,
    })
  }, [
    staffUpdateContractSpecialConditionDueDate,
    campaignId,
    contractId,
    conditionId,
    clause?.clauseId,
    localDueDate,
    saveState,
  ])

  const onDueDateChange = useCallback((newDate: Date | null) => {
    setLocalDueDate(newDate ?? undefined)
  }, [])

  return (
    <>
      <AgxSpacer fill direction="vertical" gap="md">
        <AgxTypography
          type="body_ui"
          size="small"
          id={`clause-${clause.clauseId}`}
          color="var(--text-secondary)"
        >
          Clause {clause.clausePosition}: {clause.clauseText}
        </AgxTypography>
        <AgxSpacer
          direction="horizontal"
          fill
          contentAlignment="end"
          gap="spacing-xl"
        >
          <AgxDatePicker
            id={`clause-${clause.clauseId}-date`}
            placeholder="No Due Date"
            disabled={!isEditing}
            required
            label="Due Date"
            stretch
            optionalText=""
            value={localDueDate}
            onChange={onDueDateChange}
          />

          {isEditing ? (
            <AgxSaveChangesButton
              state={saveState}
              id={`clause-${clause.clauseId}-save-button`}
              text={saveButtonText}
              variant={'primary'}
              onClick={onSaveClick}
            />
          ) : (
            <AgxButton
              id={`clause-${clause.clauseId}-button`}
              text={'Edit'}
              variant={'hollow'}
              size="medium"
              onClick={onEditClick}
            />
          )}
        </AgxSpacer>
      </AgxSpacer>
      {!isLastClause && <AgxDivider variant={'horizontal'} />}
    </>
  )
}

export default SpecialConditionClause
